// Define your storage keys as constants for type safety
export const StorageKeys = {
    ASO_INFORMATION: 'ASO_INFORMATION',
} as const;

export type StorageKey = typeof StorageKeys[keyof typeof StorageKeys];

// ============= Configuration =============
const config = {
    prefix: 'meu-pgr-web_',
    enableLogging: true,
};

export const configure = (options:  Partial<typeof config>) => {
    Object.assign(config, options);
};

// ============= Helper Functions =============
const addPrefix = (key: StorageKey): string => {
    return config. prefix + key;
};

const isPrimitive = (value: any): boolean => {
    return typeof value === 'string' ||
        typeof value === 'number' ||
        typeof value === 'boolean';
};

// ============= Core Storage Operations =============
const setItem = <T>(storage: Storage, key:  StorageKey, value: T): void => {
    try {
        if (value === null || value === undefined) {
            storage.removeItem(addPrefix(key));
            return;
        }
        const valueToStore = isPrimitive(value) ? String(value) : JSON.stringify(value);
        storage.setItem(addPrefix(key), valueToStore);

        if (config.enableLogging) {
            console.log(`[Storage] Set ${addPrefix(key)}: `, value);
        }
    } catch (error) {
        console.error(`Error storing item with key "${key}":`, error);
    }
};

const getItem = <T>(storage: Storage, key:  StorageKey): T | null => {
    try {
        const item = storage.getItem(addPrefix(key));
        if (item === null) return null;

        try {
            return JSON.parse(item) as T;
        } catch {
            return item as T;
        }
    } catch (error) {
        console.error(`Error retrieving item with key "${key}": `, error);
        return null;
    }
};

const removeItem = (storage: Storage, key: StorageKey): void => {
    try {
        storage. removeItem(addPrefix(key));
    } catch (error) {
        console.error(`Error removing item with key "${key}":`, error);
    }
};

// ============= Public API =============
export const local = {
    set: <T>(key:  StorageKey, value: T) => setItem(localStorage, key, value),
    get: <T>(key: StorageKey) => getItem<T>(localStorage, key),
    remove: (key: StorageKey) => removeItem(localStorage, key),
    clear: () => localStorage.clear(),
    has: (key: StorageKey) => localStorage.getItem(addPrefix(key)) !== null,
    keys: () => Object.keys(localStorage).filter(k => k.startsWith(config.prefix)),
};

export const session = {
    set: <T>(key: StorageKey, value: T) => setItem(sessionStorage, key, value),
    get: <T>(key: StorageKey) => getItem<T>(sessionStorage, key),
    remove: (key: StorageKey) => removeItem(sessionStorage, key),
    clear: () => sessionStorage.clear(),
    has: (key: StorageKey) => sessionStorage.getItem(addPrefix(key)) !== null,
    keys: () => Object.keys(sessionStorage).filter(k => k.startsWith(config.prefix)),
};